import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import {
  AlertTriangle, RefreshCw, Search, X, Phone, Mail, CalendarDays, MapPin,
  Ticket, Ship, CircleSlash, Anchor, Users, Loader2, SlidersHorizontal, Check,
  MessageSquare, ChevronUp, Sparkles, Info, AlertCircle, XCircle, ShieldCheck, Plus, Smartphone, Pencil, Gift, Link2, CreditCard
} from 'lucide-react';
import { collectInPerson, isTapToPaySupported } from '../utils/tapToPay';
import { formatLongDateForLocale, localeForCountry } from '../utils/locale';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Skeleton } from './ui/skeleton';
import { MultiChipTabs } from './ui/chip-tabs';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import ServiceToggle from './ServiceToggle';
import ReconcilePaymentSection from './ReconcilePaymentSection';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from './ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose
} from './ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription
} from './ui/dialog';
import apiV2, { orgApi } from '../utils/apiV2';
import { toast } from 'sonner';
import { hapticMedium, hapticSuccess } from '../utils/haptics';
import { formatCreatedAt, createdLabel } from '../utils/formatCreatedAt';
import TeamAssignmentSelector from './TeamAssignmentSelector';
import EditBookingModal from './EditBookingModal';
import { resolveTeamSlots, getTeamMembersWithStatus, getAssignedCrew, shortCrewName } from '../utils/teamAssignments';
import { BookingTimeline } from './BookingTimeline';

/* ------------------------------- helpers -------------------------------- */

const ymd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const parseDay = (s) => {
  const [y, m, d] = (s || '').split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const toMin = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const fmtShort = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'pm' : 'am';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hh}:${String(m).padStart(2, '0')}${ap}` : `${hh}${ap}`;
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hh}:${String(m).padStart(2, '0')} ${ap}` : `${hh} ${ap}`;
};

const fmtDur = (mins) => {
  if (!mins) return '';
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const money = (n) => {
  // Org currency is set once at login (localStorage.orgCurrency) — no per-render refetch.
  const cur = (typeof window !== 'undefined' ? localStorage.getItem('orgCurrency') : null) || 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
  } catch {
    return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

const dayHeading = (s) => {
  const d = parseDay(s);
  return {
    wd: d.toLocaleDateString(undefined, { weekday: 'long' }),
    md: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  };
};

const isToday = (s) => ymd(new Date()) === s;
const isPastDay = (s) => s < ymd(new Date());
const isCancelled = (b) => ['cancelled', 'canceled'].includes((b.status || '').toLowerCase());

const isRefunded = (b) => ['refunded', 'partially_refunded', 'partial_refund'].includes((b.paymentStatus || '').toLowerCase());

const statusCategory = (b) => {
  if (isCancelled(b)) return 'cancelled';
  const ps = (b.paymentStatus || '').toLowerCase();
  const st = (b.status || '').toLowerCase();
  if (isRefunded(b)) return 'refunded';
  if (ps === 'deposit_paid') return 'deposit';
  if (ps === 'partial') return 'partial';
  if (['paid', 'completed', 'comp', 'complimentary'].includes(ps) || st === 'completed') return 'paid';
  return 'unpaid';
};

// blue = good · amber = keep an eye on it · red = cancelled
const STATUS_META = {
  paid:      { label: 'Paid',             pill: 'bg-blue-50 text-blue-700',  dot: 'bg-blue-500' },
  deposit:   { label: 'Security Deposit', pill: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  partial:   { label: 'Partial Paid',     pill: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  unpaid:    { label: 'Unpaid',           pill: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  refunded:  { label: 'Refunded',         pill: 'bg-purple-50 text-purple-700', dot: 'bg-purple-500' },
  cancelled: { label: 'Cancelled',        pill: 'bg-red-50 text-red-600 line-through', dot: 'bg-red-500' },
};

const FILTER_TABS = [
  { value: 'paid', label: 'Paid' },
  { value: 'deposit', label: 'Security Deposit' },
  { value: 'partial', label: 'Partial Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'cancelled', label: 'Cancelled' },
];
const DEFAULT_FILTERS = ['paid', 'deposit', 'partial', 'unpaid'];
const UNASSIGN = '__unassign__';
const ORIG_UNAVAIL = '__original_unavailable__';
const PAST_DAYS_INIT = 30, FUTURE_DAYS_INIT = 120, PAST_CAP = 540, FUTURE_CAP = 720;

const activeAssetsOnly = (assets) => assets.filter((a) =>
  a.active !== false && !['inactive', 'retired', 'deleted', 'archived'].includes((a.status || '').toLowerCase()));

const balanceOf = (b) => {
  const total = Number(b.totalAmount ?? b.totalPrice ?? 0);
  const paid = Number(b.amountPaid ?? b.paidAmount ?? 0);
  let due = b.remainingBalance != null ? Number(b.remainingBalance) : (total - paid);
  // the serializer reports remainingBalance=0 for never-paid bookings — trust the
  // math so unpaid trips still surface the "Collect payment" actions
  const ps = (b.paymentStatus || '').toLowerCase();
  const settled = ['paid', 'completed', 'comp', 'complimentary', 'refunded', 'partially_refunded', 'partial_refund'].includes(ps);
  if (due <= 0 && paid < total && !settled) due = total - paid;
  return { total, paid, due: Math.max(0, due) };
};

// where did the booking come from? (read-only origin badge)
const originLabel = (b) => {
  const s = `${b.source || ''} ${b.bookingSource || ''}`.toLowerCase();
  if (s.includes('getmyboat') || s.includes('gmb')) return 'GetMyBoat';
  if (s.includes('boatsetter')) return 'Boatsetter';
  if (s.includes('widget') || s.includes('direct') || s.includes('website')) return 'Website';
  return 'Manual';
};

// AI boat-suggestion → compact inline chip. Only the confident GREEN pick gets a
// text chip (it names the boat to assign); amber/red are shown via the row OUTLINE.
const suggestionChip = (s) => {
  if (!s) return null;
  if (s.status === 'green' && s.primary) return { c: 'bg-blue-100 text-blue-700', label: s.primary.assetName };
  return null;
};

const SUG_META = {
  green: { wrap: 'border-blue-200 bg-blue-50', iconC: 'text-blue-600', titleC: 'text-blue-700' },
  amber: { wrap: 'border-amber-200 bg-amber-50', iconC: 'text-amber-600', titleC: 'text-amber-800' },
  red:   { wrap: 'border-rose-200 bg-rose-50', iconC: 'text-rose-600', titleC: 'text-rose-800' },
};

const getScrollParent = (node) => {
  let el = node?.parentElement;
  while (el) {
    const s = window.getComputedStyle(el);
    if (/(auto|scroll|overlay)/.test(s.overflowY) && el.scrollHeight > el.clientHeight) return el;
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

/* ------------------------------ subviews -------------------------------- */

const StatusPill = ({ cat, className = 'px-2.5 py-0.5 text-[11px]' }) => {
  const m = STATUS_META[cat] || STATUS_META.unpaid;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium ${m.pill} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
};

/* ---- GetMyBoat integrations dropdown helpers ---- */
const relTime = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const gmbProviderNote = (status) => {
  const p = status?.providers?.find((x) => x.key === 'getmyboat');
  if (!p || !p.connected) return 'Not connected';
  if (p.status === 'auth_error') return 'Reconnect needed';
  return `Synced ${relTime(p.lastSyncedAt)} · ${p.importedCount || 0} imported`;
};

const BetaTag = ({ className = '' }) => (
  <span
    title="Beta — this feature is new and we're actively refining it"
    className={`inline-flex items-center rounded-full bg-indigo-50 px-1 py-0 text-[8px] font-semibold normal-case leading-tight tracking-wide text-indigo-500 ${className}`}
  >
    Beta
  </span>
);

const ProviderRow = ({ logo, name, active, note, tip, beta }) => (
  <div className="flex items-center gap-3 rounded-lg px-1 py-2" title={!active && tip ? tip : undefined}>
    <div className="h-8 w-8 shrink-0 overflow-hidden rounded-lg bg-slate-50 ring-1 ring-slate-200">
      <img src={logo} alt={name} className="h-full w-full object-contain" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5 truncate text-sm font-medium text-slate-800">
        {name}{beta && <BetaTag />}
      </div>
      <div className="truncate text-xs font-normal text-slate-400">{note}</div>
    </div>
    {active ? (
      <span className="relative flex h-2.5 w-2.5 shrink-0" title="Active" aria-label="active">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
      </span>
    ) : (
      <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-rose-400" title={tip || 'Inactive'} aria-label="inactive" />
    )}
  </div>
);

const GMB_LOGO_SM = '/images/integrations/getmyboat-logo.png';

// Tiny GetMyBoat "needs a look-see" marker shown on a booking row. CRUD events from
// GMB email sync that we can't safely auto-apply: a customer cancellation (red) or a
// post-confirmation change request (amber). Tap the row to review in the detail sheet.
const GmbFlagBadge = ({ booking, className = '' }) => {
  const cancel = booking?.gmbCancelRequested;
  const update = !cancel && booking?.gmbUpdateRequested;
  if (!cancel && !update) return null;
  const Icon = cancel ? XCircle : AlertCircle;
  const cls = cancel ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700';
  const label = cancel
    ? 'Cancelled on GetMyBoat — tap to review'
    : 'Change requested on GetMyBoat — verify (may be out of sync)';
  return (
    <span
      title={label}
      data-testid={`gmb-flag-${cancel ? 'cancel' : 'update'}-${booking.id}`}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${cls} ${className}`}
    >
      <img src={GMB_LOGO_SM} alt="" className="h-3 w-3 rounded-full object-cover" />
      <Icon className="h-3 w-3" />
    </span>
  );
};

// Normalize a booking's add-ons (standard + custom) into a single display list.
const getAddOns = (b) => {
  const norm = (a, custom) => ({
    name: a?.name || a?.title || 'Add-on',
    price: Number(a?.price || 0),
    qty: Number(a?.quantity || a?.qty || 1),
    custom,
  });
  const items = [];
  (Array.isArray(b?.addOns) ? b.addOns : []).forEach((a) => items.push(norm(a, false)));
  (Array.isArray(b?.customAddOns) ? b.customAddOns : []).forEach((a) => items.push(norm(a, true)));
  return items;
};

// Minimalist "there are add-ons on this trip" indicator for the manifest row.
const AddOnsBadge = ({ booking, className = '' }) => {
  const count = getAddOns(booking).length;
  if (!count) return null;
  return (
    <span
      data-testid={`home-slot-addons-${booking.id}`}
      title={`${count} add-on${count > 1 ? 's' : ''} on this trip — open the trip to view`}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-medium text-teal-700 ${className}`}
    >
      <Gift className="h-3 w-3" />
      {count > 1 && <span className="sm:hidden">{count}</span>}
      <span className="hidden sm:inline">{count > 1 ? `${count} add-ons` : 'Add-on'}</span>
    </span>
  );
};



const reconcileDate = (ymdStr) => {
  if (!ymdStr) return '';
  const d = parseDay(ymdStr);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

// Manual reconciliation modal — lists active partner trips so the operator can
// confirm each one still matches our system (no internal sync details leaked).
const GmbReconcileModal = ({ open, onClose, list, loading, busyId, onAction, onEdit, lockOpen }) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o && !lockOpen) onClose(); }}>
    <DialogContent className="max-w-lg" data-testid="gmb-reconcile-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-lg font-medium">
          <ShieldCheck className="h-5 w-5 text-slate-500" />
          Manual reconciliation
        </DialogTitle>
        <DialogDescription className="text-sm font-normal text-slate-500">
          Sometimes updates from bookings can fall out of sync. Review your upcoming trips below and confirm each one is still accurate — mark anything that was cancelled or verify it's still active so everything stays in sync with our system.
        </DialogDescription>
      </DialogHeader>
      <div className="max-h-[55vh] space-y-2 overflow-y-auto py-1">
        {loading ? (
          [0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)
        ) : (list || []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="gmb-reconcile-empty">
            <Check className="mb-2 h-8 w-8 text-emerald-500" />
            <div className="text-sm font-medium text-slate-700">Everything is in sync</div>
            <div className="mt-1 text-xs font-normal text-slate-400">No trips need review right now.</div>
          </div>
        ) : (list || []).map((b) => {
          const reconciled = !!b.gmbReconciledAt;
          return (
            <div key={b.id} data-testid={`gmb-reconcile-row-${b.id}`}
              className={`rounded-xl border p-3 ${reconciled ? 'border-slate-100 bg-slate-50/60' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-800">{b.customerName || 'Guest'}</div>
                  {b.serviceName && <div className="truncate text-xs font-normal text-slate-400">{b.serviceName}</div>}
                  <div className="mt-0.5 text-xs font-normal tabular-nums text-slate-500">
                    {reconcileDate(b.bookingDate)} · {fmtShort(b.startTime)}{b.endTime ? ` – ${fmtShort(b.endTime)}` : ''}
                  </div>
                </div>
                {reconciled && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                    <Check className="h-2.5 w-2.5" /> Reviewed
                  </span>
                )}
              </div>
              <div className="mt-2.5 flex gap-2">
                {!reconciled && (
                  <>
                  <Button size="sm" variant="destructive" disabled={busyId === b.id}
                    data-testid={`gmb-reconcile-cancel-${b.id}`}
                    onClick={() => onAction(b.id, 'cancel')} className="h-8 flex-1 text-xs">
                    {busyId === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Mark cancelled'}
                  </Button>
                  <Button size="sm" variant="outline" disabled={busyId === b.id}
                    data-testid={`gmb-reconcile-verify-${b.id}`}
                    onClick={() => onAction(b.id, 'verify')} className="h-8 flex-1 gap-1.5 text-xs">
                    <ShieldCheck className="h-3.5 w-3.5" /> Still active
                  </Button>
                  </>
                )}
                <Button size="sm" variant="outline" disabled={busyId === b.id}
                  data-testid={`gmb-reconcile-edit-${b.id}`}
                  onClick={() => onEdit?.(b)} className="h-8 flex-1 gap-1.5 text-xs">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </DialogContent>
  </Dialog>
);

const SlotRow = ({ b, conflict, suggestion, onClick }) => {
  const cat = statusCategory(b);
  const unassigned = !b.assetId;
  const chip = unassigned ? suggestionChip(suggestion) : null;
  const sugStatus = unassigned ? suggestion?.status : null;
  // Row OUTLINE carries the at-a-glance signal (replaces amber/red text chips):
  //  rose  = original boat gone OR AI couldn't match OR customer CANCELLED on GetMyBoat
  //  amber = a boat collision OR AI alternative OR a GetMyBoat change-request to verify
  const tone =
    ((unassigned && b.originalAssetUnavailable) || sugStatus === 'red' || b.gmbCancelRequested) ? 'rose'
      : (conflict || sugStatus === 'amber' || b.gmbUpdateRequested) ? 'amber'
        : 'neutral';
  const toneCls = {
    rose: 'bg-rose-50 hover:bg-rose-100/70',
    amber: 'bg-amber-50 hover:bg-amber-100/70',
    neutral: 'bg-white hover:bg-slate-50',
  }[unassigned ? 'rose' : tone];
  return (
    <button
      data-testid={`home-slot-${b.id}`}
      onClick={() => onClick(b)}
      className={`group flex w-full items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left shadow-[0_8px_24px_rgba(0,0,0,0.04)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)] ${toneCls}`}
    >
      <div className="min-w-0 flex-1">
        {/* TIME + badges — top line (badges live up here so rows stay exactly two levels) */}
        <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium tabular-nums text-slate-700">
          <span className="whitespace-nowrap">
            {fmtShort(b.startTime)} <span className="text-slate-300">→</span> {fmtShort(b.endTime)}
          </span>
          {tone !== 'neutral' && (
            <AlertTriangle
              data-testid={conflict ? 'home-slot-collision' : `home-slot-attention-${b.id}`}
              className={`h-3.5 w-3.5 shrink-0 ${tone === 'rose' ? 'text-rose-500' : 'text-amber-500'}`}
              aria-label={conflict ? 'boat collision' : 'needs attention'} />
          )}
          <GmbFlagBadge booking={b} />
          <AddOnsBadge booking={b} />
          {b.assetId && b.assetCapacity != null && b.partySize > b.assetCapacity && (
            <span data-testid={`home-slot-overcap-${b.id}`}
              title={`Over capacity — ${b.assetName || 'this boat'} seats ${b.assetCapacity}, party is ${b.partySize} (${b.partySize - b.assetCapacity} over). Owner-approved.`}
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" /> +{b.partySize - b.assetCapacity}<span className="hidden sm:inline"> over</span>
            </span>
          )}
          {chip && (
            <span data-testid={`home-slot-suggestion-${b.id}`}
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${chip.c}`}>
              <Sparkles className="h-3 w-3" /> {chip.label}
            </span>
          )}
        </div>
        {/* name · service · crew — second line, truncates instead of wrapping */}
        <div className="mt-0.5 flex min-w-0 items-baseline gap-1.5">
          <span className="shrink-0 whitespace-nowrap text-sm font-medium text-slate-800">{b.customerName || 'Guest'}</span>
          {b.serviceName && <span className="min-w-0 truncate text-xs font-normal text-slate-400">· {b.serviceName}</span>}
        </div>
      </div>
      <span className="shrink-0 self-center"><StatusPill cat={cat} className="px-2 py-0.5 text-[10px]" /></span>
    </button>
  );
};

const AssetGroup = ({ name, unassigned, bookings, conflictIds, suggestions, teamMembers = [], roles = [], assets = [], allBookings = [], onSelect, crewSuggestEnabled = true }) => {
  // Crew rides with the boat — unique "First L." names across the group's trips.
  const crew = [...new Map(
    bookings.flatMap((b) => getAssignedCrew(b)).map((c) => [c.id || c.name, c])
  ).values()];
  // Crew suggestion — first trip in the group with an open slot + a free role-matching member
  // (same deterministic engine as the detail sheet). Tapping opens that trip's sheet.
  const crewSuggest = useMemo(() => {
    if (!teamMembers.length) return null;
    for (const b of bookings) {
      const asset = b.assetId ? assets.find((a) => a.id === b.assetId) : null;
      const slots = resolveTeamSlots(b, asset, roles);
      const idx = slots.findIndex((s) => !s.teamId);
      if (idx === -1) continue;
      const slot = slots[idx];
      const roleIds = slot.roleIds || (slot.roleId ? [slot.roleId] : []);
      const taken = new Set(slots.filter((s) => s.teamId).map((s) => s.teamId));
      const member = getTeamMembersWithStatus({
        teamMembers, roles, roleIds,
        date: b.bookingDate, startTime: b.startTime, durationMinutes: b.durationMinutes,
        allBookings, currentBookingId: b.id,
      }).find((m) => m.available && !taken.has(m.id));
      if (member) return { booking: b, member, roleName: slot.roleName };
    }
    return null;
  }, [bookings, assets, roles, teamMembers, allBookings]);
  return (
  <div className="space-y-2">
    <div className={`flex items-center gap-1.5 px-0.5 text-[11px] font-medium uppercase tracking-wide ${unassigned ? 'text-slate-500' : 'text-slate-400'}`}>
      {unassigned ? <CircleSlash className="h-3.5 w-3.5" /> : <Ship className="h-3.5 w-3.5" />}
      {name}
      {crew.length > 0 && (
        <span data-testid={`home-group-crew-${name}`} title={`Crew: ${crew.map((c) => c.name).join(', ')}`}
          className="text-slate-500">
          + {crew.map((c) => shortCrewName(c.name)).join(', ')}
        </span>
      )}
      {crewSuggestEnabled && crewSuggest && (
        <button type="button" data-testid={`home-group-crew-suggest-${name}`}
          title={`${crewSuggest.member.name} is free${crewSuggest.roleName ? ` (${crewSuggest.roleName})` : ''} — tap to review & assign`}
          onClick={() => onSelect(crewSuggest.booking)}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-indigo-500 transition-colors hover:bg-indigo-100">
          <Sparkles className="h-3 w-3" /> {shortCrewName(crewSuggest.member.name)}
        </button>
      )}
    </div>
    <div className="space-y-2">
      {bookings.map((b) => (
        <SlotRow key={b.id} b={b} conflict={conflictIds.has(b.id)} suggestion={suggestions?.[b.id]} onClick={onSelect} />
      ))}
    </div>
  </div>
  );
};

const DaySection = React.forwardRef(({ date, groups, conflictIds, suggestions, teamMembers, roles, assets, allBookings, onSelect, crewSuggestEnabled = true }, ref) => {
  const { wd, md } = dayHeading(date);
  const today = isToday(date);
  const past = isPastDay(date);
  return (
    <div ref={ref} className={`flex scroll-mt-24 gap-3 transition-opacity sm:gap-5 ${past ? 'opacity-50' : 'opacity-100'}`} data-testid={`home-day-${date}`}>
      <div className="w-[74px] shrink-0 pr-2 pt-1 sm:w-24 sm:pr-0">
        <div className={`text-xs font-medium leading-tight sm:text-sm ${today ? 'text-blue-600' : 'text-slate-700'}`}>{wd}</div>
        <div className={`text-xs font-normal ${today ? 'text-blue-400' : 'text-slate-400'}`}>{md}</div>
        {today && <span className="mt-1.5 inline-block rounded-full bg-blue-600 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wide text-white">Today</span>}
      </div>
      <div className="min-w-0 flex-1 space-y-4">
        {groups.map((g) => (
          <AssetGroup key={g.key} name={g.name} unassigned={g.unassigned}
            bookings={g.bookings} conflictIds={conflictIds} suggestions={suggestions}
            teamMembers={teamMembers} roles={roles} assets={assets} allBookings={allBookings} onSelect={onSelect}
            crewSuggestEnabled={crewSuggestEnabled} />
        ))}
      </div>
    </div>
  );
});
DaySection.displayName = 'DaySection';

const DetailRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3 py-2.5">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
    <div className="min-w-0 flex-1">
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-normal text-slate-800 break-words">{children}</div>
    </div>
  </div>
);

const CollectIcon = ({ icon: Icon, label, sent, busy, disabled, onClick, testid, badge }) => (
  <button
    type="button" data-testid={testid} onClick={onClick} disabled={disabled || busy || sent}
    className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors disabled:opacity-60 ${
      sent ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
    }`}
  >
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : sent ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
    {sent ? `${label} sent` : label}
    {badge && !sent && !busy && (
      <span className="ml-0.5 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{badge}</span>
    )}
  </button>
);

const SuggestionCard = ({ suggestion, suggesting, onSuggest, onAssign, booking, assigning }) => {
  if (!suggestion) {
    return (
      <Button
        variant="outline" size="sm" data-testid="home-detail-suggest-btn"
        onClick={() => onSuggest(booking)} disabled={suggesting}
        className="mt-2 h-8 gap-1.5 rounded-lg border-0 bg-blue-50 text-xs font-medium text-blue-700 shadow-none hover:bg-blue-100"
      >
        {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        {suggesting ? 'Thinking…' : 'Suggest boat'}
      </Button>
    );
  }
  const m = SUG_META[suggestion.status] || SUG_META.amber;
  const reCheck = (
    <button
      type="button" data-testid="home-detail-suggest-rerun"
      onClick={() => onSuggest(booking)} disabled={suggesting}
      title="Re-check" aria-label="Re-check suggestion"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-blue-500 transition-colors hover:bg-blue-100 disabled:opacity-60"
    >
      {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
    </button>
  );
  return (
    <div data-testid="home-detail-suggestion" className={`mt-2 rounded-lg p-2.5 ${m.wrap}`}>
      <div className="flex items-start gap-2">
        {suggestion.status === 'green'
          ? <Sparkles className={`mt-0.5 h-4 w-4 shrink-0 ${m.iconC}`} />
          : <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${m.iconC}`} />}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          <div className={`text-xs font-medium ${m.titleC}`}>
            {suggestion.status === 'green' && suggestion.primary
              ? (<><span className="font-semibold">{suggestion.primary.assetName}</span> looks like a great fit for this trip — assign it?</>)
              : suggestion.message}
          </div>

          <div className="flex w-full flex-wrap items-center gap-2">
            {suggestion.status === 'green' && suggestion.primary && (
              <Button
                size="sm" data-testid="home-detail-suggest-assign"
                onClick={() => onAssign(booking, suggestion.primary.assetId)} disabled={assigning}
                className="h-8 gap-1.5 rounded-lg bg-blue-600 text-xs font-medium text-white hover:bg-blue-700"
              >
                {assigning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Assign {suggestion.primary.assetName}
              </Button>
            )}

            {suggestion.status === 'amber' && suggestion.alternatives?.length > 0 && (
              suggestion.alternatives.map((alt) => (
                <button
                  key={alt.assetId} type="button" data-testid={`home-detail-suggest-alt-${alt.assetId}`}
                  onClick={() => onAssign(booking, alt.assetId)} disabled={assigning}
                  aria-label={`Assign ${alt.assetName}${alt.capacity ? ` (${alt.capacity} seats)` : ''}`}
                  className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-60"
                >
                  <Ship className="h-3 w-3" /> {alt.assetName}{alt.capacity ? ` · ${alt.capacity}` : ''}
                </button>
              ))
            )}

            {suggestion.status === 'red' && (
              <div data-testid="home-detail-suggest-referout"
                className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-medium text-rose-700">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Review your assets, or consider rebooking, a gift card, refund, or another option.
              </div>
            )}

            {reCheck}
          </div>
        </div>
      </div>
    </div>
  );
};

const fmtRecapDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  const country = (typeof window !== 'undefined' ? localStorage.getItem('orgCountry') : null) || 'US';
  const loc = localeForCountry(country);
  if (loc.countryCode === 'US' || loc.countryCode === 'CA') {
    const day = d.getDate();
    const ord = (n) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return s[(v - 20) % 10] || s[v] || s[0]; };
    return `${d.toLocaleDateString('en-US', { month: 'long' })} ${day}${ord(day)}, ${d.getFullYear()}`;
  }
  return formatLongDateForLocale(d, loc);
};

const RECAP_SRC = {
  getmyboat: { logo: '/images/integrations/getmyboat-logo.png', label: 'GetMyBoat' },
  bookingflows: { logo: '/logo-icon.png', label: 'BookingFlows' },
};

const RecapRow = ({ it, accent, suffix, onClick }) => {
  const src = RECAP_SRC[(it.source || 'bookingflows').toLowerCase()] || RECAP_SRC.bookingflows;
  const sub = [it.serviceName, it.confirmationCode].filter(Boolean).join(' · ');
  return (
  <button
    type="button" data-testid={`home-recap-row-${it.id}`} onClick={onClick}
    title={typeof suffix === 'string' && suffix ? `${src.label}${suffix ? ` · ${suffix}` : ''}` : src.label}
    className={`flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-lg border px-2.5 py-1.5 text-left transition-colors
      ${accent === 'blue' ? 'border-blue-100 bg-blue-50/60 hover:bg-blue-50'
        : accent === 'amber' ? 'border-amber-100 bg-amber-50/70 hover:bg-amber-50'
          : 'border-slate-100 bg-slate-50/70 hover:bg-slate-50'}`}
  >
    <img src={src.logo} alt={src.label} className="h-5 w-5 shrink-0 rounded-full object-cover" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm text-slate-800">
        <span className="font-semibold">{it.customerName || 'Guest'}</span>
        <span className="font-normal text-slate-400"> · {fmtRecapDate(it.date)}</span>
      </div>
      {sub && <div className="truncate text-[11px] font-normal text-slate-400">{sub}</div>}
    </div>
    {suffix && (
      <span className={`flex max-w-[40%] shrink-0 items-center gap-1 overflow-hidden text-xs font-medium
        ${accent === 'blue' ? 'text-blue-700' : accent === 'amber' ? 'text-amber-700' : 'text-slate-500'}`}>
        {accent !== 'amber' && <span className="shrink-0">→</span>}
        <span className="min-w-0 truncate">{suffix}</span>
      </span>
    )}
  </button>
  );
};

const RecapModal = ({ recap, open, onClose, onDone, onOpenBooking }) => {
  if (!recap) return null;
  const auto = recap.autoAssigned || [];
  const need = recap.needsAttention || [];
  const others = recap.others || [];
  const bf = recap.sources?.bookingflows || 0;
  const gmb = recap.sources?.getmyboat || 0;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="home-recap-modal"
        className="max-h-[82vh] w-[calc(100vw-2rem)] max-w-sm gap-3 overflow-y-auto overflow-x-hidden rounded-2xl p-4 sm:p-5 [&>*]:min-w-0">
        <DialogHeader className="min-w-0 space-y-1 text-left">
          <DialogTitle className="text-base font-semibold text-slate-900">Welcome back 👋</DialogTitle>
          <DialogDescription className="break-words text-xs font-normal text-slate-500">
            {recap.total} new {recap.total === 1 ? 'booking' : 'bookings'} while you were away
            {(bf > 0 || gmb > 0) && (
              <span className="text-slate-400">
                {bf > 0 ? ` · ${bf} BookingFlows` : ''}{gmb > 0 ? ` · ${gmb} GetMyBoat` : ''}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {auto.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
              <Sparkles className="h-3.5 w-3.5" /> We assigned {auto.length} {auto.length === 1 ? 'boat' : 'boats'}
            </div>
            <div className="space-y-1.5">
              {auto.map((it) => (
                <RecapRow key={it.id} it={it} accent="blue"
                  suffix={[it.assetName || 'boat', it.crewName ? `+ ${it.crewName}` : null].filter(Boolean).join(' · ')}
                  onClick={() => onOpenBooking(it)} />
              ))}
            </div>
          </div>
        )}

        {need.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5" /> {need.length} {need.length === 1 ? 'trip needs' : 'trips need'} a boat
            </div>
            <div className="space-y-1.5">
              {need.map((it) => (
                <RecapRow key={it.id} it={it} accent="amber" suffix="needs a boat" onClick={() => onOpenBooking(it)} />
              ))}
            </div>
          </div>
        )}

        {others.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
              <Check className="h-3.5 w-3.5" /> {others.length} {others.length === 1 ? 'booking' : 'bookings'} all set
            </div>
            <div className="space-y-1.5">
              {others.map((it) => (
                <RecapRow key={it.id} it={it} accent="slate" suffix={it.assetName || ''} onClick={() => onOpenBooking(it)} />
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button data-testid="home-recap-done" onClick={onDone}
            className="h-10 w-full rounded-xl bg-blue-600 text-sm font-medium text-white hover:bg-blue-700">
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InfoRow = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-3 text-sm" data-testid={testid}>
    <span className="w-20 shrink-0 font-normal text-slate-400">{label}</span>
    <span className="min-w-0 flex-1 text-right font-normal text-slate-700">{children}</span>
  </div>
);

const SYSTEM_QS = new Set(['email', 'phone', 'name', 'firstName', 'lastName', 'partySize', 'selectedAddOns',
  'customAnswers', 'referralCode', 'distributionRef', 'migration', 'promoCode', 'currency',
  'conversionRate', 'isMultiCurrency', 'presentmentCurrency']);

// "Additional information" — source, edit history, discount, gift card, add-ons and
// custom questions, with the full booking timeline behind a flat "see more" toggle.
const AdditionalInfo = ({ b }) => {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { setExpanded(false); }, [b?.id]);
  const isGmb = `${b.bookingSource || b.source || ''}`.toLowerCase().includes('getmyboat');
  const srcLogo = (isGmb ? RECAP_SRC.getmyboat : RECAP_SRC.bookingflows).logo;
  const addOns = getAddOns(b);
  const questions = (b.customAnswers || []).filter((q) => q && q.question && !SYSTEM_QS.has(q.question)
    && q.answer !== null && q.answer !== undefined && q.answer !== '' && q.answer !== false);
  const editedStr = (() => {
    if (!b.ownerModifiedAt) return '';
    const d = new Date(b.ownerModifiedAt);
    return isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  })();
  const discountRaw = b.discountCode || b.promoCode;
  const discountCode = (discountRaw && typeof discountRaw === 'object') ? (discountRaw.code || '') : discountRaw;

  const rows = [
    <InfoRow key="source" label="Source" testid="home-detail-origin">
      <span className="inline-flex max-w-full items-center gap-1.5">
        <img src={srcLogo} alt="" className="h-3.5 w-3.5 shrink-0 rounded-full object-cover" />
        <span className="min-w-0 truncate">{originLabel(b)}</span>
      </span>
    </InfoRow>,
  ];
  if (b.ownerModified) {
    rows.push(
      <InfoRow key="edited" label="Last edited" testid="home-detail-owner-edited">
        {editedStr || '—'}{b.ownerModifiedBy ? ` · ${b.ownerModifiedBy}` : ''}
      </InfoRow>
    );
  }
  if (discountCode || b.discountAmount > 0) {
    rows.push(
      <InfoRow key="discount" label="Discount" testid="home-detail-discount">
        {discountCode || 'Applied'}{b.discountAmount > 0 ? ` · −${money(b.discountAmount)}` : ''}
      </InfoRow>
    );
  }
  if (b.giftCard?.code) {
    rows.push(
      <InfoRow key="gift" label="Gift card" testid="home-detail-gift-card">
        {b.giftCard.code}{b.giftCard.amount > 0 ? ` · −${money(b.giftCard.amount)}` : ''}
      </InfoRow>
    );
  }
  addOns.forEach((a, i) => rows.push(
    <InfoRow key={`addon-${i}`} label={i === 0 ? 'Add-ons' : ''} testid={`home-detail-addon-${i}`}>
      {a.name}{a.qty > 1 ? ` × ${a.qty}` : ''}{a.price > 0 ? ` · ${money(a.price * (a.qty || 1))}` : ''}
    </InfoRow>
  ));
  questions.forEach((q, i) => rows.push(
    <div key={`q-${i}`} className="text-sm" data-testid={`home-detail-question-${i}`}>
      <div className="font-normal text-slate-400">{q.question}</div>
      <div className="font-normal text-slate-700">{String(q.answer)}</div>
    </div>
  ));

  const visible = expanded ? rows : rows.slice(0, 2);
  return (
    <div className="flex items-start gap-3 py-2.5" data-testid="home-detail-additional-info">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Additional information</div>
        <div className="mt-1 space-y-1.5">{visible}</div>
        {expanded && (
          <div className="mt-2.5" data-testid="home-detail-timeline">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Timeline</div>
            <BookingTimeline booking={b} />
          </div>
        )}
        <button type="button" data-testid="home-detail-additional-toggle" onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 text-xs font-medium text-blue-600 transition-colors hover:text-blue-700">
          {expanded ? 'see less' : '… see more'}
        </button>
      </div>
    </div>
  );
};

const BookingDetailSheet = ({ booking, assets, bookings, assigning, teamMembers = [], roles = [], onAssignCrew, assigningCrew, onAssign, onClose, onEdit, reminder, sending, onSendReminder, onTapToPay, tapping, onCopyPayLink, copyingLink, suggestion, suggesting, onSuggest, aiSuggestEnabled, crewSuggestEnabled = true, onMarkOriginalUnavailable, onDismissGmbFlag, onConfirmGmbCancel, gmbActionBusy, readOnly = false }) => {
  const [notesExpanded, setNotesExpanded] = useState(false);
  useEffect(() => { setNotesExpanded(false); }, [booking?.id]);
  const [showReconcile, setShowReconcile] = useState(false);
  useEffect(() => { setShowReconcile(false); }, [booking?.id]);
  const b = booking;
  // swipe-to-close (mobile): drag the sheet to the right to dismiss
  const [tx, setTx] = useState(0);
  const swipe = useRef({ x: 0, y: 0, mode: null, tx: 0 });
  // Crew assignment — local slot state (seeded from the booking + its boat's roles),
  // plus a "notify crew" toggle (default ON, operator can flip it per change).
  const crewAsset = b?.assetId ? assets.find((a) => a.id === b.assetId) : null;
  const [crewSlots, setCrewSlots] = useState(() => resolveTeamSlots(b, crewAsset, roles));
  const [notifyCrew, setNotifyCrew] = useState(true);
  useEffect(() => {
    const asset = b?.assetId ? assets.find((a) => a.id === b.assetId) : null;
    setCrewSlots(resolveTeamSlots(b, asset, roles));
  }, [b?.id, b?.assetId, assets, roles]);
  const getCrewWithStatus = useCallback((roleIds) => getTeamMembersWithStatus({
    teamMembers, roles, roleIds,
    date: b?.bookingDate, startTime: b?.startTime, durationMinutes: b?.durationMinutes,
    allBookings: bookings, currentBookingId: b?.id,
  }), [teamMembers, roles, b?.bookingDate, b?.startTime, b?.durationMinutes, bookings, b?.id]);
  const handleCrewChange = (idx, value) => {
    const member = teamMembers.find((m) => m.id === value);
    const next = crewSlots.map((s, i) => i === idx
      ? { ...s, teamId: value === 'unassigned' ? '' : value, teamName: value === 'unassigned' ? '' : (member?.name || '') }
      : s);
    setCrewSlots(next);
    onAssignCrew?.(b, next, notifyCrew);
  };
  // Deterministic crew suggestion — first open slot + first crew member who
  // matches the role and is free for this window (same engine as the dropdown).
  const crewSuggestion = useMemo(() => {
    const idx = crewSlots.findIndex((s) => !s.teamId);
    if (idx === -1) return null;
    const slot = crewSlots[idx];
    const roleIds = slot.roleIds || (slot.roleId ? [slot.roleId] : []);
    const taken = new Set(crewSlots.filter((s) => s.teamId).map((s) => s.teamId));
    const member = getCrewWithStatus(roleIds).find((m) => m.available && !taken.has(m.id));
    return member ? { idx, slot, member } : null;
  }, [crewSlots, getCrewWithStatus]);
  const onTouchStart = (e) => { const t = e.touches[0]; swipe.current = { x: t.clientX, y: t.clientY, mode: null, tx: 0 }; };
  const onTouchMove = (e) => {
    const s = swipe.current; const t = e.touches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (s.mode === null && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) s.mode = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    if (s.mode === 'h' && dx > 0) { s.tx = dx; setTx(dx); }
  };
  const onTouchEnd = () => { const s = swipe.current; if (s.mode === 'h' && s.tx > 90) onClose(); s.tx = 0; s.mode = null; setTx(0); };
  if (!b) return null;
  const cat = statusCategory(b);
  const { wd, md } = dayHeading(b.bookingDate);
  const { total, paid, due } = balanceOf(b);
  const balanceDue = due > 0.01 && !isCancelled(b);
  const tapNative = isTapToPaySupported();
  const createdAtStr = formatCreatedAt(b.createdAt);

  // collision FYI — FUTURE/today trips only; resolves live as the boat is reassigned
  const collision = !!b.assetId && !isPastDay(b.bookingDate) && bookings.some((o) =>
    o.id !== b.id && o.assetId === b.assetId && o.bookingDate === b.bookingDate && !isCancelled(o)
    && toMin(o.startTime) < toMin(b.endTime) && toMin(o.endTime) > toMin(b.startTime));

  const options = activeAssetsOnly(assets);
  if (b.assetId && !options.some((a) => a.id === b.assetId)) {
    options.unshift({ id: b.assetId, name: b.assetName || 'Current boat', capacity: null });
  }

  const emailSent = reminder.id === b.id && reminder.email;
  const smsSent = reminder.id === b.id && reminder.sms;

  return (
    <Sheet open={!!b} onOpenChange={(o) => { if (!o && !window.__bfTourActive) onClose(); }}>
      <SheetContent side="right" data-testid="home-detail-sheet"
        onPointerDownOutside={(e) => { if (window.__bfTourActive) e.preventDefault(); }}
        onInteractOutside={(e) => { if (window.__bfTourActive) e.preventDefault(); }}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        style={{ transform: tx ? `translateX(${tx}px)` : undefined, transition: tx ? 'none' : undefined }}
        className="w-full overflow-y-auto sm:max-w-md [&>button]:hidden pt-[calc(env(safe-area-inset-top,0px)+24px)] pb-[calc(env(safe-area-inset-bottom,0px)+24px)]">
        <SheetHeader className="pr-12 text-left">
          <SheetClose
            data-testid="home-detail-close"
            className="absolute right-4 top-[calc(env(safe-area-inset-top,0px)+16px)] flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <X className="h-4 w-4" /><span className="sr-only">Close</span>
          </SheetClose>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill cat={cat} />
          </div>
          <SheetTitle className="text-xl font-medium">{b.customerName || 'Guest'}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-baseline gap-x-2 font-mono text-xs font-medium text-blue-600">
            <span>{b.confirmationCode}</span>
            {createdAtStr && (
              <span className="font-sans text-[10px] font-normal text-slate-400" data-testid="home-detail-created-at">{createdLabel(b)} {createdAtStr}</span>
            )}
          </SheetDescription>
        </SheetHeader>

        {/* GetMyBoat CRUD flags — events from email sync we can't safely auto-apply.
            We surface them for the operator to reconcile (BookingFlows is not the GMB
            source of truth). Cancel = customer cancelled on GMB; Update = post-booking
            change request with no confirmation of the new details. */}
        {b.gmbCancelRequested && !isCancelled(b) && (
          <div data-testid="home-detail-gmb-cancel" className="mt-3 rounded-xl bg-rose-50 p-3">
            <div className="flex items-start gap-2">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-rose-800">Cancelled on GetMyBoat</div>
                <div className="mt-0.5 text-xs font-normal text-rose-700">
                  {b.gmbCancelCustomer || 'The customer'} cancelled this booking on GetMyBoat. We didn’t change it automatically — confirm how you’d like to handle it here.
                </div>
              </div>
            </div>
            {!readOnly && (
            <div className="mt-2.5 flex gap-2">
              <Button size="sm" variant="destructive" disabled={gmbActionBusy}
                data-testid="home-detail-gmb-cancel-confirm"
                onClick={() => onConfirmGmbCancel?.(b)} className="h-8 flex-1 text-xs">
                {gmbActionBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Cancel this booking'}
              </Button>
              <Button size="sm" variant="outline" disabled={gmbActionBusy}
                data-testid="home-detail-gmb-cancel-keep"
                onClick={() => onDismissGmbFlag?.(b, 'cancel')} className="h-8 flex-1 border-0 bg-white text-xs shadow-none hover:bg-rose-100/60">
                Keep it
              </Button>
            </div>
            )}
          </div>
        )}
        {b.gmbUpdateRequested && !b.gmbCancelRequested && !isCancelled(b) && (
          <div data-testid="home-detail-gmb-update" className="mt-3 rounded-xl bg-amber-50 p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-amber-800">Change requested on GetMyBoat</div>
                <div className="mt-0.5 text-xs font-normal text-amber-700">
                  {b.gmbUpdateCustomer || 'The customer'} requested a change on GetMyBoat after booking. We can’t confirm the new details — this trip may be out of sync. Open it on GetMyBoat, then edit here to match.
                </div>
              </div>
            </div>
            {!readOnly && (
            <div className="mt-2.5 flex gap-2">
              <Button size="sm" variant="outline" disabled={gmbActionBusy}
                data-testid="home-detail-gmb-update-verified"
                onClick={() => onDismissGmbFlag?.(b, 'update')} className="h-8 flex-1 gap-1.5 border-0 bg-white text-xs shadow-none hover:bg-amber-100/60">
                <ShieldCheck className="h-3.5 w-3.5" /> Mark verified
              </Button>
            </div>
            )}
          </div>
        )}

        <div className="divide-y divide-transparent">
          {/* BOAT — assign / reassign / unassign right here */}
          <div className="flex items-start gap-3 py-2.5">
            <Ship className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Boat {assigning && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
              </div>
              {readOnly ? (
                <div className="mt-1 text-sm font-normal text-slate-700" data-testid="home-detail-boat-readonly">
                  {b.assetName || 'No boat assigned yet'}
                </div>
              ) : (
              <Select value={b.assetId || UNASSIGN}
                onValueChange={(v) => {
                  if (v === ORIG_UNAVAIL) return onMarkOriginalUnavailable?.(b);
                  onAssign(b, v === UNASSIGN ? null : v);
                }} disabled={assigning}>
                <SelectTrigger data-testid="home-detail-boat-select" className="mt-1 h-9 border-0 bg-slate-100/80 font-normal shadow-none focus:ring-1 focus:ring-slate-200">
                  <SelectValue placeholder="Assign a boat" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGN} data-testid="home-detail-boat-unassign">No boat (unassign)</SelectItem>
                  {options.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`home-detail-boat-${a.id}`}>
                      {a.name}{a.capacity ? ` · ${a.capacity} seats` : ''}
                    </SelectItem>
                  ))}
                  <SelectItem value={ORIG_UNAVAIL} data-testid="home-detail-boat-original-unavailable"
                    className="text-rose-600 focus:text-rose-700">
                    Original Asset Unavailable
                  </SelectItem>
                </SelectContent>
              </Select>
              )}
              {b.originalAssetUnavailable ? (
                <div data-testid="home-detail-original-unavailable" className="mt-1.5 flex items-start gap-2 rounded-lg bg-rose-50 p-2.5">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                  <div className="min-w-0 flex-1 text-xs">
                    <span className="font-medium text-rose-700">Original boat unavailable</span>
                    <span className="font-normal text-rose-600"> — pick a new boat above, or refer the trip out.</span>
                  </div>
                  <button type="button" data-testid="home-detail-original-unavailable-clear"
                    onClick={() => onMarkOriginalUnavailable?.(b, false)} disabled={assigning || readOnly}
                    className="shrink-0 text-[11px] font-medium text-rose-400 transition-colors hover:text-rose-700 disabled:opacity-60">
                    Dismiss
                  </button>
                </div>
              ) : (!b.assetId && <div className="mt-1 text-xs font-normal text-blue-600">{readOnly ? '' : 'Needs a boat — pick one above'}</div>)}
              {!readOnly && !b.assetId && (aiSuggestEnabled || b.originalAssetUnavailable) && (
                <SuggestionCard
                  suggestion={suggestion} suggesting={suggesting} onSuggest={onSuggest}
                  onAssign={onAssign} booking={b} assigning={assigning}
                />
              )}
              {collision && (
                <div data-testid="home-detail-collision" className="mt-1.5 flex items-start gap-1.5 text-xs font-light text-amber-600">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>Booking collision — this boat is also used at this time. Reassign either trip to resolve.</span>
                </div>
              )}
              {b.assetId && b.assetCapacity != null && b.partySize > b.assetCapacity && (
                <div data-testid="home-detail-overcap" className="mt-1.5 flex items-start gap-1.5 text-xs font-light text-amber-600">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>Over capacity — {b.assetName || 'this boat'} seats {b.assetCapacity}, this party is {b.partySize} ({b.partySize - b.assetCapacity} over). Assigned anyway.</span>
                </div>
              )}
            </div>
          </div>

          {/* CREW — assign / reassign / unassign right here. Only shown when the
              boat has crew roles (or crew is already assigned); otherwise hidden
              so trips without crew requirements stay clean. */}
          {crewSlots.length > 0 && (
            <div className="flex items-start gap-3 py-2.5" data-testid="home-detail-crew">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    Crew {assigningCrew && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
                  </div>
                  {!readOnly && (
                  <div className="flex items-center gap-1.5 text-[11px] font-normal text-slate-500" title="Notify assigned & unassigned crew by SMS/email">
                    Notify crew
                    <ServiceToggle checked={notifyCrew} onChange={setNotifyCrew} data-testid="home-detail-crew-notify" />
                  </div>
                  )}
                </div>
                {readOnly ? (
                  <div className="mt-1 space-y-0.5 text-sm font-normal text-slate-700" data-testid="home-detail-crew-readonly">
                    {crewSlots.map((s, i) => (
                      <div key={i}>
                        {s.teamName || <span className="text-slate-400">Unassigned</span>}
                        {s.roleName && <span className="text-slate-400"> · {s.roleName}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="mt-2">
                  <TeamAssignmentSelector
                    teamAssignments={crewSlots}
                    roles={roles}
                    getTeamWithStatus={getCrewWithStatus}
                    onTeamChange={handleCrewChange}
                    disabled={assigningCrew}
                    testIdPrefix="home-detail-crew-assign"
                    triggerClassName="border-0 bg-slate-100/80 shadow-none focus:ring-1 focus:ring-slate-200"
                  />
                </div>
                )}
                {!readOnly && crewSuggestEnabled && crewSuggestion && (
                  <div data-testid="home-detail-crew-suggestion" className="mt-2 rounded-lg bg-blue-50 p-2.5">
                    <div className="flex items-start gap-2">
                      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-blue-700">
                          <span className="font-semibold">{crewSuggestion.member.name}</span> is free to run this trip{crewSuggestion.slot.roleName ? ` as ${crewSuggestion.slot.roleName}` : ''} — assign?
                        </div>
                        <Button size="sm" data-testid="home-detail-crew-suggest-assign" disabled={assigningCrew}
                          onClick={() => handleCrewChange(crewSuggestion.idx, crewSuggestion.member.id)}
                          className="mt-2 h-8 gap-1.5 rounded-lg bg-blue-600 text-xs font-medium text-white hover:bg-blue-700">
                          {assigningCrew ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          Assign {shortCrewName(crewSuggestion.member.name)}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}


          <DetailRow icon={Anchor} label="Service">{b.serviceName || '—'}</DetailRow>
          <DetailRow icon={CalendarDays} label="When">{wd}, {md} · {fmtTime(b.startTime)} – {fmtTime(b.endTime)} {b.durationMinutes ? `(${fmtDur(b.durationMinutes)})` : ''}</DetailRow>
          <DetailRow icon={Users} label="Party size">{b.partySize || '—'} guests</DetailRow>

          {b.customerPhone && <DetailRow icon={Phone} label="Phone"><a className="text-blue-600 underline" href={`tel:${b.customerPhone}`}>{b.customerPhone}</a></DetailRow>}
          {b.customerEmail && <DetailRow icon={Mail} label="Email"><a className="text-blue-600 underline break-all" href={`mailto:${b.customerEmail}`}>{b.customerEmail}</a></DetailRow>}
          {b.pickupLocation && <DetailRow icon={MapPin} label="Pickup">{b.pickupLocation}</DetailRow>}

          {/* PAYMENT — Total / Paid / Balance due  (neutral / blue / amber) */}
          {total > 0 && (
            <div className="flex items-start gap-3 py-2.5" data-testid="home-detail-payment">
              <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Payment</div>
                <div className="mt-1 space-y-1 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-normal text-slate-500">Total trip</span>
                    <span className="font-medium text-slate-700" data-testid="home-detail-total">{money(total)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-normal text-slate-500">Paid</span>
                    <span className="font-medium text-blue-600" data-testid="home-detail-paid">{money(paid)}</span>
                  </div>
                  {balanceDue && (
                    <div className="flex items-center justify-between">
                      <span className="font-normal text-amber-600">Balance due</span>
                      <span className="font-medium text-amber-600" data-testid="home-detail-due">{money(due)}</span>
                    </div>
                  )}
                </div>

                {balanceDue && !readOnly && (
                  <div className="mt-3">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Collect payment</div>
                    <div className="mt-1.5 flex gap-2">
                      <CollectIcon testid="home-collect-email" icon={Mail} label="Email" sent={emailSent}
                        busy={sending === 'email'} disabled={!b.customerEmail} onClick={() => onSendReminder(b, 'email')} />
                      <CollectIcon testid="home-collect-sms" icon={MessageSquare} label="SMS" sent={smsSent}
                        busy={sending === 'sms'} disabled={!b.customerPhone} onClick={() => onSendReminder(b, 'sms')} />
                      <CollectIcon testid="home-collect-link" icon={Link2} label="Copy link"
                        busy={copyingLink} disabled={!b.confirmationCode} onClick={() => onCopyPayLink(b)} />
                    </div>
                    {tapNative && (
                      <>
                        <div className="mt-2">
                          <CollectIcon testid="home-collect-tap" icon={Smartphone} label="Tap to Pay (in person)"
                            busy={tapping} onClick={() => onTapToPay(b)} />
                        </div>
                        <p className="mt-2 text-[11px] font-light leading-relaxed text-slate-400">
                          Tap to Pay turns this iPhone into a card reader — tap the customer&rsquo;s card to collect the balance in person.
                        </p>
                      </>
                    )}
                  </div>
                )}

                {!readOnly && (
                  <div className="mt-3">
                    {!showReconcile ? (
                      <button type="button" data-testid="home-detail-reconcile-toggle"
                        onClick={() => setShowReconcile(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 transition-colors hover:text-blue-700">
                        <CreditCard className="h-3.5 w-3.5" />
                        Charge card on file
                      </button>
                    ) : (
                      <ReconcilePaymentSection orgId={localStorage.getItem('organizationId')} trip={b} />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {b.internalNotes && (
            <div className="flex items-start gap-3 py-2.5" data-testid="home-detail-notes">
              <Pencil className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Internal notes</div>
                <div className={`mt-0.5 whitespace-pre-wrap text-sm font-normal text-slate-700 ${notesExpanded ? '' : 'line-clamp-2'}`}>
                  {b.internalNotes}
                </div>
                {b.internalNotes.length > 140 && (
                  <button type="button" data-testid="home-detail-notes-toggle" onClick={() => setNotesExpanded((v) => !v)}
                    className="mt-0.5 text-xs font-medium text-blue-600 transition-colors hover:text-blue-700">
                    {notesExpanded ? 'see less' : '… see more'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Additional information — source, edit history, discounts, gift cards,
              add-ons, custom questions + the booking timeline behind "see more" */}
          <AdditionalInfo b={b} />
        </div>

        {/* View ticket + Edit booking side by side; Close as a full-width bottom CTA */}
        <SheetFooter className="mt-5 flex flex-col gap-2 sm:flex-col sm:space-x-0">
          <div className="flex gap-2">
            {b.ticketUrl && (
              <Button variant="outline" data-testid="home-detail-view-ticket" className="flex-1 border-0 bg-slate-100 text-slate-700 shadow-none hover:bg-slate-200" onClick={() => window.open(b.ticketUrl, '_blank')}>
                <Ticket className="mr-2 h-4 w-4" /> View ticket
              </Button>
            )}
            {!readOnly && (
            <Button data-testid="home-detail-edit" className="flex-1 bg-blue-600 text-white hover:bg-blue-700" onClick={() => onEdit(b)}>Edit booking</Button>
            )}
          </div>
          <SheetClose asChild>
            <Button variant="ghost" data-testid="home-detail-close-bottom" className="w-full text-slate-500 hover:text-slate-800">Close</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

/* ------------------------------- main ----------------------------------- */

export default function HomeActivity({ onNavigate, isAdmin = true, tabPermissions = {}, currentTeamId = null }) {
  // CRUD permissions for this tab — owners/admins bypass. Manifest edits go through
  // the bookings API (backend enforces PermissionChecker('bookings','update')), so a
  // grant on EITHER 'overview' or 'bookings' unlocks the editing experience here.
  const _perm = (tab, op) => {
    const p = tabPermissions?.[tab];
    return p === true || p?.[op] === true;
  };
  const canUpdate = isAdmin || _perm('overview', 'update') || _perm('bookings', 'update');
  const canCreate = isAdmin || _perm('overview', 'create') || _perm('bookings', 'create');
  const isTeamSession = typeof window !== 'undefined' && localStorage.getItem('userType') === 'teamMember';
  const MY_TRIPS_KEY = 'bf_home_my_trips';
  const [myTripsOnly, setMyTripsOnly] = useState(() => {
    if (!currentTeamId || !isTeamSession) return false;
    const stored = localStorage.getItem(MY_TRIPS_KEY);
    if (stored != null) return stored === '1';
    return !isAdmin; // team members land on their own schedule by default
  });
  const toggleMyTrips = (v) => { setMyTripsOnly(v); localStorage.setItem(MY_TRIPS_KEY, v ? '1' : '0'); };
  const [bookings, setBookings] = useState([]);
  const [assets, setAssets] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [assigningCrew, setAssigningCrew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [gmbStatus, setGmbStatus] = useState(null);
  const [gmbSyncing, setGmbSyncing] = useState(false);
  const [gmbActionBusy, setGmbActionBusy] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileList, setReconcileList] = useState([]);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileBusyId, setReconcileBusyId] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [suggesting, setSuggesting] = useState(false);
  const [suggestingId, setSuggestingId] = useState(null);
  const [aiSettings, setAiSettings] = useState({ autoSuggest: true, autoAssign: true });
  const [recap, setRecap] = useState(null);
  const [recapOpen, setRecapOpen] = useState(false);
  const [reminder, setReminder] = useState({ id: null, email: false, sms: false });
  const [tapping, setTapping] = useState(false);
  const [sending, setSending] = useState(null);  const [refreshing, setRefreshing] = useState(false);
  const [pullPx, setPullPx] = useState(0);
  const [caughtUp, setCaughtUp] = useState(false);

  const [pastDays, setPastDays] = useState(PAST_DAYS_INIT);
  const [futureDays, setFutureDays] = useState(FUTURE_DAYS_INIT);
  const pastRef = useRef(PAST_DAYS_INIT);
  const [editing, setEditing] = useState(null);
  const [services, setServices] = useState([]);
  const futureRef = useRef(FUTURE_DAYS_INIT);
  const loadingMoreRef = useRef(false);
  const anchorRef = useRef(null);

  const rootRef = useRef(null);
  const topRef = useRef(null);
  const botRef = useRef(null);
  const todayRef = useRef(null);
  const didScroll = useRef(false);

  const fetchWindow = useCallback(async (pDays, fDays, isMore) => {
    if (isMore) setLoadingMore(true); else setLoading(true);
    try {
      const start = new Date(); start.setDate(start.getDate() - pDays);
      const end = new Date(); end.setDate(end.getDate() + fDays);
      const calls = [orgApi.getBookings({ startDate: ymd(start), endDate: ymd(end), limit: 500 })];
      if (!isMore) { calls.push(orgApi.getAssets()); calls.push(orgApi.getTeamMembers()); calls.push(orgApi.getRoles()); }
      const settled = await Promise.allSettled(calls);
      if (settled[0].status === 'fulfilled') {
        const data = settled[0].value;
        setBookings(Array.isArray(data?.bookings) ? data.bookings : (Array.isArray(data) ? data : []));
      } else if (!isMore) {
        setBookings([]);
      }
      if (!isMore && settled[1]?.status === 'fulfilled') {
        const a = settled[1].value;
        setAssets(a?.data?.assets || a?.data || []);
      }
      if (!isMore && settled[2]?.status === 'fulfilled') {
        const t = settled[2].value;
        setTeamMembers(t?.data?.teamMembers || t?.data || []);
      }
      if (!isMore && settled[3]?.status === 'fulfilled') {
        const r = settled[3].value;
        setRoles(r?.data?.roles || r?.data || []);
      }
    } catch (e) {
      if (!isMore) { toast.error('Could not load activity'); setBookings([]); }
    } finally {
      if (isMore) setTimeout(() => setLoadingMore(false), 250); else setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWindow(PAST_DAYS_INIT, FUTURE_DAYS_INIT, false); }, [fetchWindow]);

  // land on today, once
  useEffect(() => {
    if (loading || didScroll.current) return;
    didScroll.current = true;
    requestAnimationFrame(() => todayRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' }));
  }, [loading, bookings]);

  // keep scroll anchored when older days are prepended
  useLayoutEffect(() => {
    const a = anchorRef.current;
    if (a?.parent) {
      const delta = a.parent.scrollHeight - a.prevHeight;
      if (delta > 0) a.parent.scrollTop += delta;
      anchorRef.current = null;
    }
  }, [bookings]);

  const loadMorePast = useCallback(() => {
    if (!showPast) return;  // past trips hidden → never fetch earlier
    if (loadingMoreRef.current || pastRef.current >= PAST_CAP) return;
    loadingMoreRef.current = true;
    const parent = getScrollParent(rootRef.current);
    anchorRef.current = parent ? { parent, prevHeight: parent.scrollHeight } : null;
    const np = Math.min(pastRef.current + 60, PAST_CAP);
    pastRef.current = np; setPastDays(np);
    fetchWindow(np, futureRef.current, true).finally(() => { loadingMoreRef.current = false; });
  }, [fetchWindow, showPast]);

  const loadMoreFuture = useCallback(() => {
    if (loadingMoreRef.current || futureRef.current >= FUTURE_CAP) return;
    loadingMoreRef.current = true;
    const nf = Math.min(futureRef.current + 120, FUTURE_CAP);
    futureRef.current = nf; setFutureDays(nf);
    fetchWindow(pastRef.current, nf, true).finally(() => { loadingMoreRef.current = false; });
  }, [fetchWindow]);

  useEffect(() => {
    if (loading) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        if (e.target === topRef.current) loadMorePast();
        else if (e.target === botRef.current) loadMoreFuture();
      });
    }, { rootMargin: '300px 0px' });
    if (topRef.current) obs.observe(topRef.current);
    if (botRef.current) obs.observe(botRef.current);
    return () => obs.disconnect();
  }, [loading, loadMorePast, loadMoreFuture]);

  // Pull-to-refresh (mobile): at the very top of the list, drag down to refetch.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    hapticMedium();
    try {
      await fetchWindow(pastRef.current, futureRef.current, true);
      hapticSuccess();
      setCaughtUp(true);
      setTimeout(() => setCaughtUp(false), 1200);
    } catch (_) { /* fetchWindow toasts on hard failure */ }
    finally { setTimeout(() => setRefreshing(false), 300); }
  }, [fetchWindow]);

  useEffect(() => {
    const scroller = getScrollParent(rootRef.current);
    if (!scroller) return;
    let startY = 0, pulling = false, dist = 0;
    const onStart = (e) => {
      if (refreshing) { pulling = false; return; }
      if (scroller.scrollTop <= 0) { startY = e.touches[0].clientY; pulling = true; dist = 0; }
      else pulling = false;
    };
    const onMove = (e) => {
      if (!pulling) return;
      if (scroller.scrollTop > 0) { pulling = false; setPullPx(0); return; }
      const dy = e.touches[0].clientY - startY;
      if (dy <= 0) { dist = 0; setPullPx(0); return; }
      dist = Math.min(dy * 0.5, 80);  // damped
      setPullPx(dist);
    };
    const onEnd = () => {
      if (!pulling) return;
      pulling = false;
      if (dist >= 56) refresh();
      setPullPx(0);
    };
    scroller.addEventListener('touchstart', onStart, { passive: true });
    scroller.addEventListener('touchmove', onMove, { passive: true });
    scroller.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      scroller.removeEventListener('touchstart', onStart);
      scroller.removeEventListener('touchmove', onMove);
      scroller.removeEventListener('touchend', onEnd);
    };
  }, [refresh, refreshing]);

  const assignBoat = async (booking, assetId) => {
    if ((booking.assetId || null) === (assetId || null)) return;
    setAssigning(true);
    try {
      await orgApi.updateBooking(booking.id, { assetId, allowOverlap: true, allowOvercapacity: true, notifyTeam: false, notifyCustomer: false });
      const asset = assets.find((a) => a.id === assetId);
      const patch = { assetId: assetId || null, assetName: asset?.name || null, assetCapacity: asset?.capacity ?? null };
      // assigning a real boat re-seats the trip → clear any 'original unavailable' flag
      if (assetId && booking.originalAssetUnavailable) {
        patch.originalAssetUnavailable = false;
        orgApi.setOriginalUnavailable(booking.id, false).catch(() => {});
      }
      setBookings((prev) => prev.map((bk) => (bk.id === booking.id ? { ...bk, ...patch } : bk)));
      setSelected((prev) => (prev && prev.id === booking.id ? { ...prev, ...patch } : prev));
      // once a boat is set, the suggestion no longer applies
      if (assetId) setSuggestions((prev) => { const n = { ...prev }; delete n[booking.id]; return n; });
      toast.success(assetId ? `Assigned to ${asset?.name || 'boat'}` : 'Boat unassigned');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === 'object' && d.message) ? d.message : (typeof d === 'string' ? d : 'Could not update the boat'));
    } finally {
      setAssigning(false);
    }
  };

  // Assign / reassign / unassign crew right from the manifest detail sheet.
  // Sends the FULL team_assignments array; the backend diffs old→new and (when
  // notify=true) messages both newly-assigned AND swapped-out crew. Payment/
  // logistics fields are untouched — this is a crew-only edit.
  const assignCrew = async (booking, teamAssignments, notify) => {
    setAssigningCrew(true);
    try {
      const cleaned = (teamAssignments || []).map((a) => ({
        ...a,
        teamId: (!a.teamId || a.teamId === 'unassigned') ? '' : a.teamId,
      }));
      await orgApi.updateBooking(booking.id, {
        teamAssignments: cleaned,
        notifyTeam: !!notify,
        notifyCustomer: false,
        allowOverlap: true,
        allowOvercapacity: true,
      });
      setBookings((prev) => prev.map((bk) => (bk.id === booking.id ? { ...bk, teamAssignments: cleaned } : bk)));
      setSelected((prev) => (prev && prev.id === booking.id ? { ...prev, teamAssignments: cleaned } : prev));
      const named = cleaned.filter((a) => a.teamId).map((a) => shortCrewName(a.teamName || (teamMembers.find((m) => m.id === a.teamId)?.name)));
      const desc = notify ? 'Crew notified' : 'Crew not notified';
      toast.success(named.length ? `Crew: ${named.join(', ')}` : 'Crew updated', { description: desc });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === 'object' && d.message) ? d.message : (typeof d === 'string' ? d : 'Could not update crew'));
    } finally {
      setAssigningCrew(false);
    }
  };


  // Flag/clear "original boat no longer available" — unassigns the trip (so it shows RED
  // and re-enters the AI flow) without ever notifying the customer. Reversible.
  const markOriginalUnavailable = async (booking, unavailable = true) => {
    setAssigning(true);
    try {
      await orgApi.setOriginalUnavailable(booking.id, unavailable);
      const patch = unavailable
        ? { assetId: null, assetName: null, originalAssetUnavailable: true }
        : { originalAssetUnavailable: false };
      setBookings((prev) => prev.map((bk) => (bk.id === booking.id ? { ...bk, ...patch } : bk)));
      setSelected((prev) => (prev && prev.id === booking.id ? { ...prev, ...patch } : prev));
      if (unavailable) {
        toast.warning('Marked: original boat no longer available', { description: 'Trip needs re-seating — re-checking for options.' });
        suggestOne({ ...booking, assetId: null });
      } else {
        toast.success('Cleared — trip no longer flagged');
      }
    } catch (e) {
      toast.error('Could not update the booking');
    } finally {
      setAssigning(false);
    }
  };

  // Dismiss a GetMyBoat flag once reconciled (keep the booking / mark a change verified).
  const dismissGmbFlag = async (booking, flag) => {
    setGmbActionBusy(true);
    try {
      await orgApi.dismissGmbFlag(booking.id, flag);
      const patch = flag === 'cancel' ? { gmbCancelRequested: false } : { gmbUpdateRequested: false };
      setBookings((prev) => prev.map((bk) => (bk.id === booking.id ? { ...bk, ...patch } : bk)));
      setSelected((prev) => (prev && prev.id === booking.id ? { ...prev, ...patch } : prev));
      toast.success(flag === 'cancel' ? 'Kept — GetMyBoat cancellation dismissed' : 'Marked verified');
    } catch (e) {
      toast.error('Could not update the booking');
    } finally {
      setGmbActionBusy(false);
    }
  };

  // Operator confirms the GetMyBoat cancellation → cancel the booking here too.
  const confirmGmbCancel = async (booking) => {
    setGmbActionBusy(true);
    try {
      await orgApi.cancelBooking(booking.id);
      const patch = { status: 'cancelled', gmbCancelRequested: false };
      setBookings((prev) => prev.map((bk) => (bk.id === booking.id ? { ...bk, ...patch } : bk)));
      setSelected((prev) => (prev && prev.id === booking.id ? { ...prev, ...patch } : prev));
      toast.success('Booking cancelled');
    } catch (e) {
      toast.error('Could not cancel the booking');
    } finally {
      setGmbActionBusy(false);
    }
  };

  // GMB Reconcile — review active GetMyBoat trips to catch host-initiated cancellations.
  const loadReconcile = useCallback(async () => {
    setReconcileLoading(true);
    try {
      const res = await orgApi.getGmbReconcile();
      setReconcileList(res.bookings || []);
    } catch (e) {
      toast.error('Could not load GetMyBoat trips');
    } finally {
      setReconcileLoading(false);
    }
  }, []);

  const openReconcile = useCallback(() => { setReconcileOpen(true); loadReconcile(); }, [loadReconcile]);

  const handleReconcileAction = useCallback(async (id, action) => {
    setReconcileBusyId(id);
    try {
      await orgApi.gmbReconcile(id, action);
      setReconcileList((prev) => prev.map((b) => (b.id === id ? { ...b, gmbReconciledAt: new Date().toISOString() } : b)));
      if (action === 'cancel') {
        setBookings((prev) => prev.map((bk) => (bk.id === id ? { ...bk, status: 'cancelled', gmbCancelRequested: false } : bk)));
      }
      toast.success(action === 'cancel' ? 'Booking cancelled' : 'Marked reviewed');
    } catch (e) {
      toast.error('Could not update the booking');
    } finally {
      setReconcileBusyId(null);
    }
  }, []);

  const coolingToast = (e) => {
    const d = e?.response?.data?.detail;
    toast.message('Suggestions cooling down', { description: (d && d.message) || 'Try again in a few minutes.' });
  };

  // batch suggestions for ALL unassigned bookings (instant/deterministic; runs on load + after a sync)
  const runBatchSuggestions = useCallback(async ({ silent = false } = {}) => {
    setSuggesting(true);
    try {
      const res = await orgApi.suggestBoats();
      const map = res?.data?.suggestions || {};
      setSuggestions((prev) => ({ ...prev, ...map }));
      const n = Object.keys(map).length;
      if (!silent && n) toast.success(`Boat ideas ready for ${n} unassigned ${n === 1 ? 'trip' : 'trips'}`);
    } catch (e) {
      if (!silent && e?.response?.status === 429) coolingToast(e);
      // otherwise stay silent — suggestions are a bonus
    } finally {
      setSuggesting(false);
    }
  }, []);

  // populate suggestion chips on first load (free, deterministic) — operators only
  useEffect(() => { if (canUpdate) runBatchSuggestions({ silent: true }); }, [runBatchSuggestions, canUpdate]);

  // on-demand suggestion for a single booking (detail sheet button)
  const suggestOne = useCallback(async (booking) => {
    setSuggestingId(booking.id);
    try {
      const res = await orgApi.suggestBoatForBooking(booking.id);
      const s = res?.data?.suggestion;
      if (s) setSuggestions((prev) => ({ ...prev, [booking.id]: s }));
      else toast.message('No suggestion available for this trip');
    } catch (e) {
      if (e?.response?.status === 429) coolingToast(e);
      else toast.error('Could not get a suggestion');
    } finally {
      setSuggestingId(null);
    }
  }, []);

  // --- AI assignment settings (toggles, all default ON) — operators only ---
  useEffect(() => {
    if (!canUpdate) return;
    orgApi.getBoatSuggestSettings().then((r) => r?.data && setAiSettings(r.data)).catch(() => {});
  }, [canUpdate]);

  const toggleAi = useCallback(async (key, val) => {
    setAiSettings((prev) => ({ ...prev, [key]: val }));
    try {
      const r = await orgApi.updateBoatSuggestSettings({ [key]: val });
      if (r?.data) setAiSettings(r.data);
      if (key === 'autoSuggest') {
        if (val) runBatchSuggestions({ silent: true });
        else setSuggestions({});
      }
    } catch (e) {
      toast.error('Could not update setting');
    }
  }, [runBatchSuggestions]);

  // --- "while you were away" recap (since last Home visit) ---
  // The "seen" watermark is only advanced when the operator explicitly taps "Got it"
  // — closing via the X just hides it for now, so the SAME recap re-appears next visit
  // (and another operator, e.g. the owner, still sees it on their own device).
  const RECAP_KEY = 'bf_home_recap_seen';
  const recapSeenTsRef = useRef(null);
  useEffect(() => {
    (async () => {
      try {
        const seen = localStorage.getItem(RECAP_KEY);
        const now = new Date().toISOString();
        if (!seen) { localStorage.setItem(RECAP_KEY, now); return; } // first visit → no recap
        const r = await orgApi.getBoatRecap(seen);
        const data = r?.data;
        if (data && data.total > 0) {
          recapSeenTsRef.current = now;   // committed only on "Got it"
          setRecap(data); setRecapOpen(true);
        } else {
          localStorage.setItem(RECAP_KEY, now); // nothing new → advance so the window stays tight
        }
      } catch (e) { /* recap is a bonus — stay silent */ }
    })();
  }, []);

  // "Got it" → acknowledge: advance the watermark so it won't show again for me.
  const acknowledgeRecap = useCallback(() => {
    if (recapSeenTsRef.current) {
      localStorage.setItem(RECAP_KEY, recapSeenTsRef.current);
      recapSeenTsRef.current = null;
    }
    setRecapOpen(false);
  }, []);

  const openRecapBooking = useCallback(async (it) => {
    const id = typeof it === 'string' ? it : it?.id;
    if (!id) return;
    setRecapOpen(false);
    const existing = bookings.find((b) => b.id === id);
    if (existing) { setSelected(existing); return; }
    // recap can surface bookings outside the loaded Home window — fetch + map to list shape
    try {
      const r = await orgApi.getBooking(id);
      const d = r?.data || {};
      setSelected({
        id: d.id || id,
        bookingDate: d.bookingDate || it?.date,
        startTime: d.startTime || it?.time,
        endTime: d.endTime,
        assetId: d.asset?.id || null,
        assetName: d.asset?.name || null,
        serviceId: d.service?.id || null,
        serviceName: d.service?.name || it?.serviceName || null,
        customerName: d.customerName || it?.customerName,
        customerEmail: d.customerEmail,
        customerPhone: d.customerPhone,
        partySize: d.partySize,
        status: d.status,
        paymentStatus: d.paymentStatus,
        totalAmount: d.totalAmount,
        totalPrice: d.totalPrice,
        amountPaid: d.amountPaid,
        remainingBalance: d.remainingBalance,
        source: d.source,
        bookingSource: it?.source || d.source,
        confirmationCode: d.confirmationCode,
        customerData: d.customerData || {},
        customAnswers: Array.isArray(d.customAnswers) ? d.customAnswers : ((d.customerData || {}).customAnswers || []),
        source: d.source,
        gmbCancelRequested: d.gmbCancelRequested,
        gmbUpdateRequested: d.gmbUpdateRequested,
        gmbCancelCustomer: d.gmbCancelCustomer,
        gmbUpdateCustomer: d.gmbUpdateCustomer,
        ownerModified: d.ownerModified,
        ownerModifiedAt: d.ownerModifiedAt,
        createdAt: d.createdAt,
        teamAssignments: d.teamAssignments || [],
      });
    } catch (e) {
      toast.error('Could not open that booking');
    }
  }, [bookings]);


  const sendReminder = async (booking, channel) => {
    setSending(channel);
    try {
      const res = await orgApi.sendPaymentReminder(booking.id, channel);
      const ok = ((res?.data ?? res)?.success) === true;
      if (ok) {
        setReminder((r) => ({
          id: booking.id,
          email: (r.id === booking.id && r.email) || channel === 'email',
          sms: (r.id === booking.id && r.sms) || channel === 'sms',
        }));
        toast.success(channel === 'email' ? 'Payment email sent' : 'Payment SMS sent');
      } else {
        toast.message('Reminder not sent', { description: 'Email/SMS may be disabled in Settings → Integrations.' });
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not send the payment reminder');
    } finally {
      setSending(null);
    }
  };

  const handleTapToPay = async (booking) => {
    if (!isTapToPaySupported()) {
      toast.info('Tap to Pay is an iPhone-app feature', {
        description: 'Open BookingFlows on your iPhone to tap a card in person. On the web, use Email or SMS to collect.',
        duration: 6000,
      });
      return;
    }
    setTapping(true);
    const tId = toast.loading('Preparing reader…');
    try {
      await collectInPerson({
        orgApi,
        booking,
        formatAmount: (a) => money(a),
        onStatus: (m) => toast.loading(m, { id: tId }),
      });
      toast.success('Paid in full ✅', { id: tId, description: 'Balance collected in person.' });
      fetchWindow(pastRef.current, futureRef.current, true);
    } catch (e) {
      if (e?.code === 'unavailable') {
        toast.message('Tap to Pay', { id: tId, description: e.message });
      } else if (e?.code === 'canceled') {
        toast.message('Payment canceled', { id: tId });
      } else {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : (e?.message || 'Tap to Pay could not complete'), { id: tId });
      }
    } finally {
      setTapping(false);
    }
  };

  const openEdit = (b) => {
    setSelected(null);
    setEditing(b);
    if (!services.length) {
      orgApi.getServices().then((r) => setServices(r?.data?.services || r?.data || [])).catch(() => {});
    }
  };
  const openDetail = (b) => { setReminder({ id: b.id, email: false, sms: false }); setSelected(b); };

  // Payment link — same magic-link the email/SMS reminders send. Copy it (and
  // open the native share sheet on mobile) so the operator can text/AirDrop it.
  const [copyingLink, setCopyingLink] = useState(false);
  const copyPayLink = async (booking) => {
    if (!booking?.confirmationCode) return;
    setCopyingLink(true);
    try {
      const r = await apiV2.post(`/public/bookings/${booking.confirmationCode}/generate-magic-link`);
      const link = r?.data?.magicLink;
      if (!link) throw new Error('no link');
      let copied = false;
      try { await navigator.clipboard.writeText(link); copied = true; } catch (_) { /* clipboard blocked */ }
      const isMobile = /Mobi|iPhone|iPad|Android/i.test(navigator.userAgent);
      if (isMobile && navigator.share) {
        try { await navigator.share({ title: `Payment link — ${booking.customerName || 'booking'}`, url: link }); } catch (_) { /* share dismissed */ }
      }
      toast.success(copied ? 'Payment link copied' : 'Payment link ready', {
        description: copied ? 'Paste it into a text, email, or AirDrop it.' : link,
      });
    } catch (e) {
      toast.error('Could not generate payment link');
    } finally {
      setCopyingLink(false);
    }
  };

  const loadGmbStatus = useCallback(async () => {
    try {
      const res = await orgApi.getGmbStatus();
      setGmbStatus(res.data);
    } catch (e) {
      // integration may simply not be configured for this org — leave status null
    }
  }, []);

  const handleGmbSync = useCallback(async () => {
    setGmbSyncing(true);
    try {
      const res = await orgApi.gmbSyncNow();
      const d = res.data || {};
      const n = d.created_count || 0;
      const c = (d.cancelled || []).length;
      const aa = (d.autoAssign && d.autoAssign.assigned) || [];
      if (n || c) toast.success(`GetMyBoat: ${n} new${c ? `, ${c} cancelled` : ''}`);
      else toast.success('GetMyBoat: up to date');
      if (aa.length) {
        const withCrew = aa.filter((x) => x.crewName).length;
        toast.success(`🚤 Auto-assigned ${aa.length} ${aa.length === 1 ? 'boat' : 'boats'}${withCrew ? ` · crew on ${withCrew}` : ''}`);
      }
      await loadGmbStatus();
      await fetchWindow(pastRef.current, futureRef.current, false);
      runBatchSuggestions();
    } catch (e) {
      toast.error(e?.response?.status === 404
        ? 'GetMyBoat is not connected for this organization'
        : 'Sync failed — please try again');
    } finally {
      setGmbSyncing(false);
    }
  }, [loadGmbStatus, fetchWindow, runBatchSuggestions]);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return bookings
      .filter((b) => [b.customerName, b.confirmationCode, b.serviceName, b.assetName].some((v) => (v || '').toLowerCase().includes(q)))
      .sort((a, b) => (a.bookingDate || '').localeCompare(b.bookingDate || ''))
      .slice(0, 8);
  }, [bookings, search]);

  const { days, conflictIds } = useMemo(() => {
    const active = bookings.filter((b) => {
      if (!(filters.includes(statusCategory(b)) || (isRefunded(b) && filters.includes('refunded')))) return false;
      if (!showPast && isPastDay(b.bookingDate)) return false;
      if (myTripsOnly && currentTeamId) {
        const assigns = b.teamAssignments || b.team_assignments || [];
        const mine = assigns.some((a) => a.teamId === currentTeamId) || b.teamMemberId === currentTeamId;
        if (!mine) return false;
      }
      return true;
    });

    // collisions: FUTURE/today only, same asset+day, overlapping, non-cancelled
    const cset = new Set();
    const byAsset = {};
    active.forEach((b) => {
      if (!b.assetId || isCancelled(b) || isPastDay(b.bookingDate)) return;
      const k = `${b.bookingDate}|${b.assetId}`;
      (byAsset[k] = byAsset[k] || []).push(b);
    });
    Object.values(byAsset).forEach((list) => {
      for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          const a = list[i], c = list[j];
          if (toMin(a.startTime) < toMin(c.endTime) && toMin(a.endTime) > toMin(c.startTime)) { cset.add(a.id); cset.add(c.id); }
        }
      }
    });

    const dayMap = {};
    active.forEach((b) => { (dayMap[b.bookingDate] = dayMap[b.bookingDate] || []).push(b); });
    const days = Object.keys(dayMap).sort().map((date) => {
      const groupMap = {};
      dayMap[date].forEach((b) => { const key = b.assetId || '__unassigned__'; (groupMap[key] = groupMap[key] || []).push(b); });
      const groups = Object.keys(groupMap).map((key) => ({
        key,
        unassigned: key === '__unassigned__',
        name: key === '__unassigned__' ? 'Unassigned' : (groupMap[key][0].assetName || 'Unassigned'),
        bookings: groupMap[key].sort((a, b) => toMin(a.startTime) - toMin(b.startTime)),
      })).sort((a, b) => (a.unassigned !== b.unassigned ? (a.unassigned ? 1 : -1) : a.name.localeCompare(b.name)));
      return { date, groups };
    });
    return { days, conflictCount: cset.size / 2, conflictIds: cset };
  }, [bookings, filters, showPast, myTripsOnly, currentTeamId]);

  const filtersActive = showPast || myTripsOnly || filters.length !== DEFAULT_FILTERS.length || !DEFAULT_FILTERS.every((f) => filters.includes(f));

  return (
    <div ref={rootRef} className="mx-auto max-w-3xl pb-24 -mt-6 lg:mt-0" data-testid="home-activity">
      {/* sticky controls — the scroll container already starts below the fixed app header, so stick to its top */}
      <div className="sticky top-0 z-30 -mx-4 rounded-2xl bg-white/90 px-4 pb-3 pt-4 backdrop-blur-md supports-[backdrop-filter]:bg-white/75">
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="home-search" value={search}
              onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              placeholder="Search a name or booking ID…"
              className="h-10 rounded-xl pl-9 pr-9 font-normal"
            />
            {search && (
              <button type="button" data-testid="home-search-clear" onClick={() => { setSearch(''); setSearchOpen(false); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X className="h-4 w-4" />
              </button>
            )}
            {searchOpen && search.trim() && (
              <div data-testid="home-search-results" className="absolute left-0 right-0 top-12 z-30 max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                {searchResults.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm font-normal text-slate-400">No matches found</div>
                ) : searchResults.map((b) => (
                  <button key={b.id} data-testid={`home-search-result-${b.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setSearch(''); setSearchOpen(false); openEdit(b); }}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-800">{b.customerName || 'Guest'}</div>
                      <div className="truncate text-xs font-normal text-slate-400">
                        <span className="font-mono">{b.confirmationCode}</span>{b.serviceName ? ` · ${b.serviceName}` : ''}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-[11px] font-normal text-slate-400">{dayHeading(b.bookingDate).md}</span>
                      <StatusPill cat={statusCategory(b)} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="home-filter-btn" aria-label="Filters"
                className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors ${
                  filtersActive ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                }`}>
                <SlidersHorizontal className="h-4 w-4" />
                {filtersActive && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-blue-600" />}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 rounded-xl p-4" data-testid="home-filter-popover">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Filter</div>
                {filtersActive && (
                  <button type="button" data-testid="home-filter-reset" onClick={() => { setFilters(DEFAULT_FILTERS); setShowPast(false); }}
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-700">Reset</button>
                )}
              </div>
              <MultiChipTabs className="mt-2" tabs={FILTER_TABS} value={filters} onChange={setFilters} removable testIdPrefix="home-filter" />
              {currentTeamId && isTeamSession && (
                <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                  <div className="min-w-0">
                    <span className="text-sm font-normal text-slate-700">Show only my trips</span>
                    <div className="text-[11px] font-normal text-slate-400">Just the trips you're assigned to</div>
                  </div>
                  <span data-testid="home-toggle-my-trips"><ServiceToggle checked={myTripsOnly} onChange={toggleMyTrips} /></span>
                </div>
              )}
              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="text-sm font-normal text-slate-700">Show past bookings</span>
                <span data-testid="home-toggle-past"><ServiceToggle checked={showPast} onChange={setShowPast} /></span>
              </div>
              {canUpdate && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <div className="flex items-center gap-1.5 px-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  <Sparkles className="h-3.5 w-3.5" /> AI assignment <BetaTag />
                </div>
                <div className="mt-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-normal text-slate-700">Boat suggestions</div>
                    <div className="text-[11px] font-normal text-slate-400">Show suggested boats on unassigned trips</div>
                  </div>
                  <span data-testid="home-toggle-ai-suggest" className="shrink-0">
                    <ServiceToggle checked={aiSettings.autoSuggest} onChange={(v) => toggleAi('autoSuggest', v)} />
                  </span>
                </div>
                <div className="mt-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-normal text-slate-700">Crew suggestions</div>
                    <div className="text-[11px] font-normal text-slate-400">Show free crew picks next to boats & in trip details</div>
                  </div>
                  <span data-testid="home-toggle-ai-suggest-crew" className="shrink-0">
                    <ServiceToggle checked={aiSettings.suggestCrew !== false} onChange={(v) => toggleAi('suggestCrew', v)} />
                  </span>
                </div>
                <div className="mt-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-normal text-slate-700">Auto-assign boats</div>
                    <div className="text-[11px] font-normal text-slate-400">Assign confident boat matches as bookings sync in</div>
                  </div>
                  <span data-testid="home-toggle-ai-autoassign" className="shrink-0">
                    <ServiceToggle checked={aiSettings.autoAssign} onChange={(v) => toggleAi('autoAssign', v)} />
                  </span>
                </div>
                <div className="mt-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-normal text-slate-700">Auto-assign crew</div>
                    <div className="text-[11px] font-normal text-slate-400">Fill open crew slots with free, role-matching members</div>
                  </div>
                  <span data-testid="home-toggle-ai-autoassign-crew" className="shrink-0">
                    <ServiceToggle checked={aiSettings.autoAssignCrew !== false} onChange={(v) => toggleAi('autoAssignCrew', v)} />
                  </span>
                </div>
              </div>
              )}
            </PopoverContent>
          </Popover>

          {!canUpdate ? (
            <Button variant="outline" size="icon" data-testid="home-refresh" onClick={refresh}
              className="h-10 w-10 shrink-0 rounded-xl border-slate-200 bg-white" aria-label="Refresh">
              <RefreshCw className={`h-4 w-4 text-slate-500 ${loading || refreshing ? 'animate-spin' : ''}`} />
            </Button>
          ) : (
          <Popover onOpenChange={(o) => { if (o) loadGmbStatus(); }}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" data-testid="home-refresh"
                className="h-10 w-10 shrink-0 rounded-xl border-slate-200 bg-white" aria-label="Refresh & integrations">
                <RefreshCw className={`h-4 w-4 text-slate-500 ${loading || gmbSyncing ? 'animate-spin' : ''}`} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 rounded-xl p-3" data-testid="home-integrations-popover">
              <div className="px-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Connected integrations</div>
              <ProviderRow logo="/logo-icon.png" name="BookingFlows" active note="Native · always in sync" />
              <ProviderRow
                logo="/images/integrations/getmyboat-logo.png" name="GetMyBoat" beta
                active={!!gmbStatus?.providers?.find((p) => p.key === 'getmyboat')?.active}
                note={gmbProviderNote(gmbStatus)}
                tip="Contact BookingFlows admin to get your GMB integration live!" />
              <div className="mt-2 border-t border-slate-100 pt-2">
                {gmbStatus?.providers?.find((p) => p.key === 'getmyboat')?.connected ? (
                  <Button onClick={handleGmbSync} disabled={gmbSyncing} data-testid="home-sync-now"
                    className="h-9 w-full rounded-lg bg-blue-600 text-sm font-medium text-white hover:bg-blue-700">
                    {gmbSyncing ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Syncing…</>) : (<><RefreshCw className="mr-2 h-4 w-4" /> Sync now</>)}
                  </Button>
                ) : (
                  <a href="mailto:ryan@bookingflows.com?subject=Activate%20GetMyBoat%20sync&body=Hi%20BookingFlows%2C%20I%27d%20like%20to%20connect%20my%20GetMyBoat%20bookings%20to%20BookingFlows."
                    data-testid="home-gmb-activate"
                    className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50">
                    <Mail className="h-4 w-4" /> Contact BookingFlows to activate
                  </a>
                )}
                {(!!gmbStatus?.providers?.find((p) => p.key === 'getmyboat')?.active
                  || bookings.some((bk) => `${bk.source || ''} ${bk.bookingSource || ''}`.toLowerCase().includes('getmyboat'))) && (
                  <>
                  <Button variant="outline" onClick={openReconcile} data-testid="home-gmb-reconcile"
                    className="mt-2 h-9 w-full rounded-lg border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    <ShieldCheck className="mr-2 h-4 w-4" /> Manual reconciliation
                  </Button>
                  <p className="mt-1.5 px-1 text-[11px] font-light leading-relaxed text-slate-400">
                    Sometimes updates from bookings can fall out of sync — we recommend a quick weekly review to make sure everything matches our system
                  </p>
                  </>
                )}
              </div>
            </PopoverContent>
          </Popover>
          )}

          {canCreate && (
          <Button
            data-testid="home-new-booking-btn"
            onClick={() => onNavigate?.('schedule', { openAddBooking: true })}
            className="h-10 shrink-0 rounded-xl bg-blue-600 px-3.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
            aria-label="New booking">
            <Plus className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">New booking</span>
          </Button>
          )}
        </div>

        {/* conflict banner removed — the subtle per-row amber triangle is enough */}
      </div>

      {/* body */}
      <div className="mt-4 space-y-8">
        {(pullPx > 0 || refreshing || caughtUp) && (
          <div className="flex items-center justify-center overflow-hidden text-xs font-medium"
            style={{ height: refreshing || caughtUp ? 32 : pullPx, transition: pullPx ? 'none' : 'height 0.25s ease' }}
            data-testid="home-pull-refresh">
            {refreshing ? (
              <span className="flex items-center gap-2 text-blue-600"><Loader2 className="h-4 w-4 animate-spin" /> Refreshing…</span>
            ) : caughtUp ? (
              <span className="flex items-center gap-1.5 text-emerald-600"><Check className="h-4 w-4" /> All caught up</span>
            ) : (
              <RefreshCw className="h-4 w-4 text-slate-400" style={{ transform: `rotate(${pullPx * 4}deg)`, opacity: Math.min(pullPx / 56, 1) }} />
            )}
          </div>
        )}
        {loading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="flex gap-3 sm:gap-5">
              <Skeleton className="h-12 w-16 rounded sm:w-24" />
              <div className="flex-1 space-y-2"><Skeleton className="h-14 w-full rounded-lg" /><Skeleton className="h-14 w-full rounded-lg" /></div>
            </div>
          ))
        ) : days.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white py-16 text-center" data-testid="home-empty">
            <Anchor className="mb-3 h-10 w-10 text-slate-300" />
            <div className="text-base font-medium text-slate-700">Nothing on the schedule</div>
            <div className="mt-1 text-sm font-normal text-slate-400">Try adjusting the filters.</div>
          </div>
        ) : (
          <>
            {showPast && pastDays < PAST_CAP && <div ref={topRef} aria-hidden="true" />}
            {showPast && loadingMore && (
              <div className="flex items-center justify-center gap-2 py-1 text-xs font-light text-slate-400" data-testid="home-loading-earlier">
                <ChevronUp className="h-3.5 w-3.5 animate-pulse" /> loading earlier bookings…
              </div>
            )}
            {days.map((d) => (
              <DaySection key={d.date} ref={isToday(d.date) ? todayRef : null} date={d.date}
                groups={d.groups} conflictIds={conflictIds} suggestions={suggestions}
                teamMembers={canUpdate ? teamMembers : []} roles={roles} assets={assets} allBookings={bookings} onSelect={openDetail}
                crewSuggestEnabled={aiSettings.suggestCrew !== false} />
            ))}
            {futureDays < FUTURE_CAP && <div ref={botRef} aria-hidden="true" />}
          </>
        )}
        {!loading && days.length > 0 && <div className="min-h-[55vh]" aria-hidden="true" />}
      </div>

      <BookingDetailSheet
        booking={selected} assets={assets} bookings={bookings} assigning={assigning}
        readOnly={!canUpdate}
        teamMembers={teamMembers} roles={roles} onAssignCrew={assignCrew} assigningCrew={assigningCrew}
        onAssign={assignBoat} onClose={() => setSelected(null)} onEdit={openEdit}
        reminder={reminder} sending={sending} onSendReminder={sendReminder}
        onTapToPay={handleTapToPay} tapping={tapping}
        onCopyPayLink={copyPayLink} copyingLink={copyingLink}
        suggestion={selected ? suggestions[selected.id] : null}
        suggesting={!!selected && suggestingId === selected.id}
        onSuggest={suggestOne}
        aiSuggestEnabled={aiSettings.autoSuggest}
        crewSuggestEnabled={aiSettings.suggestCrew !== false}
        onMarkOriginalUnavailable={markOriginalUnavailable}
        onDismissGmbFlag={dismissGmbFlag}
        onConfirmGmbCancel={confirmGmbCancel}
        gmbActionBusy={gmbActionBusy}
      />

      {editing && (
        <EditBookingModal
          isOpen={true}
          trip={editing}
          assets={assets}
          teamMembers={teamMembers}
          roles={roles}
          services={services}
          allBookings={bookings}
          onClose={() => {
            setEditing(null);
            fetchWindow(pastRef.current, futureRef.current, true);
            if (reconcileOpen) loadReconcile();
          }}
          onSave={async (bookingId, bookingData) => {
            try {
              const cleanId = (v) => (!v || v === 'unassigned') ? null : v;
              await orgApi.updateBooking(bookingId, {
                bookingDate: bookingData.date,
                startTime: bookingData.startTime,
                durationMinutes: bookingData.duration,
                internalNotes: bookingData.notes || '',
                teamAssignments: (bookingData.teamAssignments || []).map((a) => ({ ...a, teamId: cleanId(a.teamId) || '' })),
                customerName: bookingData.customerName,
                customerEmail: bookingData.customerEmail,
                customerPhone: bookingData.customerPhone,
                pickupLocation: bookingData.pickupLocation,
                paymentStatus: bookingData.paymentStatus,
                checkedIn: bookingData.checkedIn,
                timezone: bookingData.timezone,
                assetId: cleanId(bookingData.assetId),
                partySize: bookingData.partySize,
                emergencyContactNumber: bookingData.emergencyContactNumber,
                selectedAddOns: bookingData.selectedAddOns,
                city: bookingData.city,
                country: bookingData.country,
                notifyTeam: bookingData.notifyTeam ?? false,
                notifyCustomer: bookingData.notifyCustomer ?? false,
              });
              setEditing(null);
              toast.success('Booking updated successfully!');
              await new Promise((r) => setTimeout(r, 300));
              await fetchWindow(pastRef.current, futureRef.current, true);
              if (reconcileOpen) loadReconcile();
            } catch (error) {
              const d = error.response?.data?.detail;
              if (typeof d === 'object' && d?.message) toast.error(d.message);
              else if (typeof d === 'string') toast.error(d);
              else toast.error('Failed to update booking');
            }
          }}
        />
      )}

      <RecapModal recap={recap} open={recapOpen} onClose={() => setRecapOpen(false)} onDone={acknowledgeRecap} onOpenBooking={openRecapBooking} />

      <GmbReconcileModal open={reconcileOpen} onClose={() => setReconcileOpen(false)}
        list={reconcileList} loading={reconcileLoading} busyId={reconcileBusyId} onAction={handleReconcileAction}
        lockOpen={!!editing} onEdit={(b) => setEditing(b)} />
    </div>
  );
}
